/**
 * T11 (2026-05-27 extraction-layer dispatch) — Class 2 IGNORED_HEADERS_V1
 * catalog-summary banner derivation helper.
 *
 * Pure function. Takes the raw `ignored_headers_v1` field from the catalog
 * response + the dismissed state, returns whether to show the banner and
 * the filtered list of header strings to render.
 *
 * Used by ReviewTable.tsx so the visibility gate is unit-testable without
 * mounting the full 5000-line ReviewTable component. Empty array, null,
 * undefined, and non-string entries all coerce to empty.
 */

export interface Class2BannerState {
  /** True when the banner should render (non-empty list AND not dismissed). */
  show: boolean;
  /** Filtered, deduped string list to render in the banner copy. */
  list: string[];
}

/**
 * Derive the Class 2 banner state from the catalog response and dismissal flag.
 *
 *   show=true requires:
 *     - ignoredHeadersV1 is an array with at least one non-empty string entry
 *     - dismissed is false
 *
 *   The list is filtered to non-empty strings, dedup is preserved upstream
 *   (the server aggregator already dedupes via Set; this just guards against
 *   malformed payloads / edge cases like null/undefined entries).
 */
export function deriveClass2BannerState(
  ignoredHeadersV1: unknown,
  dismissed: boolean,
): Class2BannerState {
  if (!Array.isArray(ignoredHeadersV1)) {
    return { show: false, list: [] };
  }
  const list = ignoredHeadersV1.filter(
    (h): h is string => typeof h === 'string' && h.trim().length > 0,
  );
  return {
    show: list.length > 0 && !dismissed,
    list,
  };
}
